//@ts-nocheck
import {
	type Component,
	createComponent,
	createContext,
	mergeProps,
	splitProps,
	useContext,
	type ValidComponent
} from "solid-js";
import { Dynamic, type DynamicProps } from "solid-js/web";

type Recipe = {
	(props: Record<string, unknown>): Record<string, (args: { class: string }) => string>;
	variantKeys: string[];
};

type Slot<R extends Recipe> = keyof ReturnType<R>;
type SlotRecipe<R extends Recipe> = Record<Slot<R>, (args: { class: string }) => string>;

export const createStyleContext = <R extends Recipe>(recipe: R) => {
	const StyleContext = createContext<SlotRecipe<R> | null>(null);

	const withRootProvider = <P extends {}>(comp: Component<P>): ((props: P) => JSX.Element) => {
		const StyledComponent = (props: P) => {
			const [variantProps, localProps] = splitProps(props, [
				"class",
				"className",
				...recipe.variantKeys
			]);
			const slotStyles = recipe(variantProps) as Record<Slot<R>, string>;

			return (
				<StyleContext.Provider value={slotStyles}>
					<Dynamic
						component={comp}
						{...localProps}
						class={slotStyles?.["root" ?? undefined]?.({ class: variantProps.class })}
					/>
				</StyleContext.Provider>
			);
		};
		return StyledComponent;
	};

	const withProvider = <P extends { class?: string }>(comp: Component<P>, slot: Slot<R>) => {
		const Comp = (props: P) => {
			const [variantProps, dynamicProps] = splitProps(props, [
				"class",
				"className",
				...recipe.variantKeys
			]);
			const styles = recipe(variantProps) as SlotRecipe<R>;

			return (
				<StyleContext.Provider value={styles}>
					{createComponent(
						Dynamic,
						mergeProps(dynamicProps, {
							component: comp,
							class: styles?.[slot ?? undefined]?.({ class: variantProps.class })
						})
					)}
				</StyleContext.Provider>
			);
		};
		return Comp;
	};

	const withContext = <P extends {}, ComponentType = ValidComponent>(
		Component: ComponentType,
		slot?: Slot<R>
	): ((props: Partial<DynamicProps<ComponentType, P>> & { class?: string }) => JSX.Element) => {
		if (!slot) return Component;
		return (props) => {
			const styles = useContext(StyleContext);

			return createComponent(
				Dynamic,
				mergeProps(props, {
					component: Component,
					class: styles?.[slot]?.({ class: props.class })
				})
			);
		};
	};

	return {
		withRootProvider,
		withProvider,
		withContext
	};
};
